import { useCallback, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { guardLockedRounds, lockedThroughOf, programRoundLabel, type Draft } from "@heraion/shared";

import type { ProgramEditorV3Props, V3Structure } from "./types";

type Args = Pick<ProgramEditorV3Props, "draft" | "setDraft" | "structure" | "progress" | "showToast">;

/** Whether round `week` is one of the rounds 0..lockedThrough already done. */
export const roundLockOf =
  (lockedThrough: number) =>
  (week: number): boolean =>
    week >= 0 && week <= lockedThrough;

// Decision 4 (#336 slice 5, §5.2.4, §11.3): the rounds already done in a
// program in progress are read only. Every write of the editor passes
// guardLockedRounds: one that would change them is refused with a toast whose
// action is «Unlock»; a new exercise stays out of them. «Unlock» asks first,
// then holds until the editor closes. It is not an edit, so Undo does not
// lock again.
export function useRoundLock({ draft, setDraft, structure, progress, showToast }: Args) {
  const { t } = useTranslation();
  const [unlocked, setUnlocked] = useState(false);
  const [asking, setAsking] = useState(false);
  /** The last round done by the marker, -1 when none: what the bar says. */
  const marked = lockedThroughOf(draft, progress);
  const lockedThrough = unlocked ? -1 : marked;
  const lockRef = useRef(lockedThrough);
  lockRef.current = lockedThrough;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const isRoundLocked = useMemo(() => roundLockOf(lockedThrough), [lockedThrough]);

  /** The refusal: one round named, or every round done. */
  const refuse = useCallback(
    (week?: number) => {
      const unlock = { label: t("editorV3.lock.unlock", { defaultValue: "Unlock" }), onPress: () => setAsking(true) };
      showToast(
        week === undefined
          ? t("editorV3.rule.lockedStay", {
              round: programRoundLabel(lockRef.current + 1),
              defaultValue: "Done up to {{round}}: those rounds stay as they are.",
            })
          : t("editorV3.roundLocked", { n: week + 1, defaultValue: "W{{n}} is done: it stays as it is." }),
        unlock,
      );
    },
    [showToast, t],
  );

  const guarded: Dispatch<SetStateAction<Draft>> = useCallback(
    (action) => {
      const L = lockRef.current;
      if (L < 0) return setDraft(action);
      const run = (d: Draft) => (typeof action === "function" ? action(d) : action);
      // Asked on the draft on screen, for the toast; kept in the write itself.
      // The refusal is said after the action's own toast, which it replaces.
      if (guardLockedRounds(draftRef.current, run(draftRef.current), L) === null) {
        void Promise.resolve().then(() => refuse());
        return;
      }
      setDraft((d) => guardLockedRounds(d, run(d), L) ?? d);
    },
    [setDraft, refuse],
  );

  // The two structural writes that do not pass setDraft.
  const lockedStructure: V3Structure = useMemo(
    () => ({
      ...structure,
      removeDay: (idx: number) => {
        const d = draftRef.current;
        const next = { ...d, sessions: d.sessions.filter((_, i) => i !== idx) };
        if (guardLockedRounds(d, next, lockRef.current) === null) refuse();
        else structure.removeDay(idx);
      },
      changeCycleWeeks: (weeks: number, grow?: "clone" | "blank") => {
        if (weeks <= lockRef.current) refuse();
        else structure.changeCycleWeeks(weeks, grow);
      },
    }),
    [structure, refuse],
  );

  return {
    lockedThrough,
    isRoundLocked,
    marked,
    unlocked,
    asking,
    ask: () => setAsking(true),
    cancel: () => setAsking(false),
    unlock: () => {
      setUnlocked(true);
      setAsking(false);
    },
    refuse,
    setDraft: guarded,
    structure: lockedStructure,
  };
}

export type RoundLock = ReturnType<typeof useRoundLock>;
